import { useState, useEffect } from "react";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { useTheme } from "@/hooks/useTheme";
import { CheckCircle2, X, Smartphone, WifiOff, Zap, Layout, Share } from "lucide-react";

export function PwaInstallBanner({ variant = "banner" }) {
  const { isInstallable, isInstalled, installApp } = usePwaInstall();
  const { theme } = useTheme();
  const [isDismissed, setIsDismissed] = useState(false);
  const [showManualGuide, setShowManualGuide] = useState(false);
  
  const isIframe = typeof window !== "undefined" && window.self !== window.top;

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem("driplee_install_dismissed");
      if (dismissed === "true") {
        setIsDismissed(true);
      }
    } catch {}
  }, []);

  // If already installed or dismissed, do not render
  if (isInstalled || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem("driplee_install_dismissed", "true");
    } catch {}
    setIsDismissed(true);
  };

  const handleInstallClick = async () => {
    if (isIframe) {
      setShowManualGuide(true);
      return;
    }
    const success = await installApp();
    if (!success) {
      // Fallback: show manual install instructions (especially for iOS Safari and other browsers)
      setShowManualGuide(true);
    }
  };

  const logoSrc = theme === "night" ? "/driplee-loader-dark.webp" : "/driplee-loader-light.webp";

  const benefits = [
    {
      icon: <WifiOff className="h-4 w-4 text-[#2AA7A0] dark:text-[#36CDC5]" />,
      title: "Full Offline Capability",
      desc: "Instant access in remote locations where signals drop.",
    },
    {
      icon: <Zap className="h-4 w-4 text-[#2AA7A0] dark:text-[#36CDC5]" />,
      title: "Fast 1-Tap Launch",
      desc: "Launches instantly from your home screen just like a native app.",
    },
    {
      icon: <Layout className="h-4 w-4 text-[#2AA7A0] dark:text-[#36CDC5]" />,
      title: "Zero-Distraction UI",
      desc: "Standalone view hides the browser address bar for maximum screen area and focus.",
    },
  ];

  if (variant === "home-section") {
    return (
      <div className="w-full max-w-3xl mx-auto my-12 p-6 md:p-8 rounded-[2.5rem] bg-gradient-to-br from-white/80 to-teal-50/20 dark:from-slate-900/60 dark:to-teal-950/10 border border-[#2AA7A0]/20 dark:border-[#36CDC5]/10 shadow-lg relative overflow-hidden text-left">
        {/* Background ambient glow */}
        <div className="absolute -right-16 -top-16 w-36 h-36 bg-[#2AA7A0]/10 dark:bg-[#36CDC5]/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col gap-6">
          <div className="flex-1 space-y-4">
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#0A3D5F] dark:text-[#EBF3F8] tracking-tight">
                Install Driplee on Your Device
              </h2>
              <p className="mt-1 text-sm text-[#64849A] dark:text-[#7CA1B5] font-light">
                Add Driplee to your mobile home screen for a premium, lightning-fast bedside reference.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
              {benefits.map((b, i) => (
                <div key={i} className="flex flex-col gap-1.5 p-3 rounded-2xl bg-white/40 dark:bg-slate-950/20 border border-white/40 dark:border-white/5">
                  <div className="flex items-center gap-2">
                    {b.icon}
                    <h3 className="text-xs font-semibold text-[#0A3D5F] dark:text-[#EBF3F8]">
                      {b.title}
                    </h3>
                  </div>
                  <p className="text-[11px] leading-relaxed text-[#3E6179] dark:text-[#7CA1B5] font-normal">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                onClick={handleInstallClick}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#2AA7A0] to-[#1C7F79] hover:from-[#25948E] hover:to-[#176F69] text-white px-6 py-3 text-sm font-bold shadow-md hover:scale-[1.01] active:scale-[0.99] transition"
              >
                <Smartphone className="h-4 w-4" />
                Install Now
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] transition"
              >
                Hide this section
              </button>
            </div>
          </div>
        </div>

        {/* Manual guide helper */}
        {showManualGuide && (
          <div className="mt-6 p-4 rounded-2xl border border-[#2AA7A0]/20 bg-[#2AA7A0]/5 dark:bg-[#2AA7A0]/10 text-[#0A3D5F] dark:text-teal-200 animate-fadeIn">
            <div className="flex justify-between items-start gap-4">
              <div className="space-y-3">
                {isIframe && (
                  <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/5 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200">
                    <h4 className="text-xs font-bold uppercase tracking-wider mb-1">IFrame Preview Detected</h4>
                    <p className="text-[11px] leading-relaxed font-light">
                      Browsers block interactive app installation inside nested frame previews. To install Driplee on your device, please click the <strong>"Open in New Tab"</strong> button in the top-right header of your AI Studio environment, and then click <strong>Install Now</strong>.
                    </p>
                  </div>
                )}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-2">How to install manually:</h4>
                  <ul className="text-xs space-y-2 list-disc pl-4 font-light">
                    <li>
                      <strong>For iOS Safari:</strong> Tap the Share button <Share className="inline h-3 w-3 mx-0.5" /> in the Safari toolbar, then scroll down and select <strong>"Add to Home Screen"</strong>.
                    </li>
                    <li>
                      <strong>For Android Chrome:</strong> Tap the options menu <span className="font-bold">⋮</span> in the top right, then select <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong>.
                    </li>
                  </ul>
                </div>
              </div>
              <button onClick={() => setShowManualGuide(false)} aria-label="Close manual install guide" className="p-1 text-[#3E6179] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8]">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default: Banner style for top of CalculatorPage
  return (
    <div className="w-full rounded-3xl border border-[#2AA7A0]/20 dark:border-[#36CDC5]/20 bg-gradient-to-r from-[#EAF6F7] to-[#E6EFF4] dark:from-[#112735] dark:to-[#12222E] p-4 shadow-sm relative overflow-hidden mb-4">
      {/* Absolute Dismiss Cross */}
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-3 p-1 rounded-full text-[#3E6179] hover:text-[#0A3D5F] dark:hover:text-white transition"
        title="Dismiss banner"
        aria-label="Dismiss install banner"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="text-left">
        <div className="flex-1 pr-6">
          <div className="text-sm font-bold text-[#0A3D5F] dark:text-white flex items-center gap-1.5 leading-tight">
            Install Driplee Web App
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          </div>
          <p className="mt-1 text-xs text-[#3E6179] dark:text-[#7CA1B5] font-normal leading-relaxed">
            Driplee is fully offline-capable. Install it to ensure quick access in remote locations.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={handleInstallClick}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#0A3D5F] dark:bg-[#36CDC5] hover:bg-[#06283F] dark:hover:bg-[#2AA7A0] text-white dark:text-[#09141B] px-4 py-2 text-xs font-bold shadow transition-all duration-200"
            >
              <Smartphone className="h-3.5 w-3.5" />
              Install to Home Screen
            </button>
            <button
              onClick={() => setShowManualGuide(!showManualGuide)}
              className="text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-white transition"
            >
              How to add manually?
            </button>
          </div>
        </div>
      </div>

      {showManualGuide && (
        <div className="mt-3 border-t border-[#2AA7A0]/10 dark:border-[#36CDC5]/10 pt-3 text-left space-y-2">
          {isIframe && (
            <div className="bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 p-3 rounded-2xl text-amber-800 dark:text-amber-200">
              <div className="text-[11px] font-bold uppercase tracking-wider mb-1">
                IFrame Preview Active
              </div>
              <p className="text-xs leading-relaxed font-light">
                Browsers prevent 1-click app installation inside nested frame previews (iframes). To install Driplee, please click <strong>"Open in New Tab"</strong> in the top-right of your AI Studio workspace first, then click <strong>Install</strong>.
              </p>
            </div>
          )}
          <div className="bg-white/60 dark:bg-slate-950/20 p-3 rounded-2xl">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#0A3D5F] dark:text-teal-300 mb-1.5">
              Bedside Install Instructions:
            </div>
            <ul className="text-xs space-y-1.5 list-disc pl-4 text-[#3E6179] dark:text-[#7CA1B5] font-light">
              <li>
                <strong>On iPhone / iOS Safari:</strong> Tap the <Share className="inline h-3.5 w-3.5 mx-0.5 text-blue-500" /> button in Safari, then tap <strong>"Add to Home Screen"</strong>.
              </li>
              <li>
                <strong>On Android / Chrome:</strong> Click <span className="font-bold text-[#0A3D5F] dark:text-white">⋮</span> in Chrome header, then select <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong>.
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
